// Infrastructure > Updates — the image-update surface (BTCPay-style).
//
// Two sections on one page:
//   1. Control plane: is a newer cluster-manager image published, and an
//      operator-confirmed self-update (pull -> recreate -> health) via the
//      CM updater agent.
//   2. Workers: per-host image drift + drain-then-roll, one worker at a
//      time, each roll tracked as an Operation with a step timeline.
//
// This page covers the app-code/image axis only. Chromium builds and
// profile bundles keep their own release pipeline under /releases.

import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogCancel,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatRelative } from "@veil/ui/lib/format";
import CircleArrowUp from "lucide-react/dist/esm/icons/circle-arrow-up";
import MoveRight from "lucide-react/dist/esm/icons/move-right";
import RefreshCcw from "lucide-react/dist/esm/icons/refresh-ccw";
import { useState } from "react";
import { useLocation } from "wouter";
import { hasRole, useAuth } from "@/lib/auth-context";
import {
	type UpdateVia,
	useRefreshUpdateChecks,
	useSelfUpdate,
	useSelfUpdateCheck,
	useWorkerRoll,
	useWorkerUpdates,
	type WorkerUpdateRow,
} from "@/lib/updates/hooks";

/** How a worker gets rolled, stated plainly rather than as a config verdict. */
function WorkerUpdateMethod({ via }: { via: UpdateVia }) {
	if (via === "socket") {
		return (
			<Badge variant="secondary" title="Managed by this cluster-manager">
				Managed here
			</Badge>
		);
	}
	if (via === "agent") {
		return (
			<Badge variant="secondary" title="Rolled by this host's updater agent">
				Updater agent
			</Badge>
		);
	}
	return (
		<Badge
			variant="outline"
			title="Registered by hand. Deploy an updater agent on that host, or set updaterAgentUrl/Token, to roll it from here."
		>
			Manual
		</Badge>
	);
}

function workerUpdateHint(w: WorkerUpdateRow): string | undefined {
	if (w.desiredState === "stopped") {
		return "Worker is stopped — start it before rolling its image";
	}
	if (!w.canUpdate) {
		return "This host was registered by hand; it needs an updater agent to be rolled from here";
	}
	return undefined;
}

export function Updates() {
	const { user } = useAuth();
	const canOperate = hasRole(user, "operator");
	const [, navigate] = useLocation();

	const self = useSelfUpdateCheck();
	const workers = useWorkerUpdates();
	const refreshChecks = useRefreshUpdateChecks();

	const [selfConfirmOpen, setSelfConfirmOpen] = useState(false);
	const [workerConfirm, setWorkerConfirm] = useState<WorkerUpdateRow | null>(
		null,
	);

	const selfUpdate = useSelfUpdate({
		onStarted: (opId) => navigate(`/operations/${opId}`),
	});
	const workerRoll = useWorkerRoll({
		onStarted: (opId) => navigate(`/operations/${opId}`),
	});

	const check = self.data?.check;
	const selfConfigured = self.data?.selfUpdateConfigured ?? false;
	const checking = self.isLoading || self.isFetching;
	const checkedAt = self.dataUpdatedAt > 0 ? self.dataUpdatedAt : null;

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Updates</h1>
					<p className="text-sm text-muted-foreground">
						Software updates for the cluster-manager and worker container
						images. Chromium builds and profiles live under Releases.
					</p>
				</div>
				<div className="flex items-center gap-2">
					{checkedAt ? (
						<span
							className="text-xs text-muted-foreground"
							title={new Date(checkedAt).toLocaleString()}
						>
							Checked {formatRelative(new Date(checkedAt))}
						</span>
					) : null}
					<Button
						variant="outline"
						size="sm"
						onClick={() => void refreshChecks()}
						disabled={checking || workers.isFetching}
						aria-label="Check for updates now"
					>
						<RefreshCcw
							className={checking ? "size-4 animate-spin" : "size-4"}
						/>
						Check now
					</Button>
				</div>
			</div>

			{/* ---- Control plane -------------------------------------------- */}
			<Card>
				<CardHeader>
					<CardTitle>Cluster manager</CardTitle>
					<CardDescription>
						The control-plane container. Updating pulls the new image, recreates
						the container and waits for health; the console reconnects
						automatically.
					</CardDescription>
				</CardHeader>
				<CardContent>
					{self.isLoading ? (
						<Skeleton className="h-12 w-full max-w-md" />
					) : self.isError ? (
						<div className="flex items-center gap-3 text-sm">
							<span className="text-destructive">
								Update check failed:{" "}
								{self.error instanceof Error
									? self.error.message
									: "unknown error"}
							</span>
							<Button
								variant="outline"
								size="sm"
								onClick={() => self.refetch()}
							>
								Retry
							</Button>
						</div>
					) : check ? (
						<div className="flex flex-wrap items-center justify-between gap-4">
							<dl className="grid grid-cols-2 gap-x-10 gap-y-1 text-sm sm:grid-cols-3">
								<div>
									<dt className="text-xs text-muted-foreground">
										Current version
									</dt>
									<dd className="flex items-center gap-2 font-mono">
										v{check.current}
										{check.updateAvailable && check.latest ? (
											<span className="inline-flex items-center gap-1 font-sans text-xs text-amber-700 dark:text-amber-300">
												<MoveRight className="size-3" aria-hidden />
												{check.latest} available
											</span>
										) : null}
									</dd>
								</div>
								<div>
									<dt className="text-xs text-muted-foreground">Channel</dt>
									<dd className="font-mono">{check.channel}</dd>
								</div>
								<div>
									<dt className="text-xs text-muted-foreground">Status</dt>
									<dd>
										{check.error ? (
											<Badge variant="outline" title={check.error}>
												Check unavailable
											</Badge>
										) : check.updateAvailable ? (
											<Badge variant="warning">Update available</Badge>
										) : (
											<Badge variant="success">Up to date</Badge>
										)}
									</dd>
								</div>
							</dl>
							<div className="flex flex-col items-end gap-1">
								<Button
									onClick={() => setSelfConfirmOpen(true)}
									disabled={
										!canOperate ||
										!selfConfigured ||
										!check.updateAvailable ||
										selfUpdate.isPending
									}
								>
									<CircleArrowUp className="size-4" />
									{selfUpdate.isPending
										? "Starting…"
										: "Update cluster manager"}
								</Button>
								{!selfConfigured ? (
									<p className="max-w-xs text-right text-xs text-muted-foreground">
										Self-update needs an updater agent: set
										<code className="mx-1">CM_UPDATER_AGENT_URL</code>+
										<code className="mx-1">CM_UPDATER_AGENT_TOKEN</code>.
									</p>
								) : null}
							</div>
						</div>
					) : null}

					{/* The locks that serialise installs, renders and publishes are
					    in-process Maps. Nothing stops a second control plane from
					    starting against the same database, and nothing would report it,
					    so the constraint is stated rather than assumed. */}
					<p className="mt-4 border-t border-border/60 pt-4 text-xs text-muted-foreground">
						Run exactly one cluster manager per cluster. Installs, publishes and
						load-balancer renders are serialised inside this process, so a
						second instance pointed at the same database would run two of them
						at once on the same worker or the same release directory.
					</p>
				</CardContent>
			</Card>

			{/* ---- Workers --------------------------------------------------- */}
			<Card>
				<CardHeader>
					<CardTitle>Workers</CardTitle>
					<CardDescription>
						Updating a worker drains traffic, rolls the container via its
						updater agent, then re-enables it. One worker at a time.
					</CardDescription>
				</CardHeader>
				<CardContent>
					{workers.data?.check.error ? (
						<p className="mb-3 text-xs text-muted-foreground">
							Registry check unavailable: {workers.data.check.error}
						</p>
					) : null}
					{workers.isLoading ? (
						<Skeleton className="h-24 w-full" />
					) : workers.isError ? (
						<div className="flex items-center gap-3 text-sm">
							<span className="text-destructive">
								Failed to load workers:{" "}
								{workers.error instanceof Error
									? workers.error.message
									: "unknown error"}
							</span>
							<Button
								variant="outline"
								size="sm"
								onClick={() => workers.refetch()}
							>
								Retry
							</Button>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Worker</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Version</TableHead>
									<TableHead className="hidden md:table-cell">
										Updater agent
									</TableHead>
									<TableHead className="w-0 text-right">
										<span className="sr-only">Actions</span>
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{(workers.data?.workers ?? []).length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={5}
											className="text-sm text-muted-foreground"
										>
											No workers registered yet. Provision one from
											Infrastructure &gt; Workers.
										</TableCell>
									</TableRow>
								) : (
									(workers.data?.workers ?? []).map((w) => {
										const rolling =
											workerRoll.isPending &&
											workerRoll.variables?.hostname === w.hostname;
										return (
											<TableRow key={w.hostname}>
												<TableCell>
													<div className="font-medium">{w.hostname}</div>
													<div className="font-mono text-xs text-muted-foreground">
														{w.publicIp}
													</div>
												</TableCell>
												<TableCell>
													{w.enabled ? (
														<Badge variant="success">In pool</Badge>
													) : (
														<Badge variant="outline">Drained</Badge>
													)}
												</TableCell>
												<TableCell>
													<span
														className="font-mono text-sm"
														title={
															w.imageDigest
																? `Pinned to ${w.imageDigest}${
																		w.imageRef ? ` (${w.imageRef})` : ""
																	}`
																: "No image digest recorded for this worker"
														}
													>
														{w.currentApiVersion ?? "—"}
													</span>
													{w.updateAvailable && w.latestImage ? (
														<span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
															<MoveRight className="size-3" aria-hidden />
															{w.latestImage}
														</span>
													) : w.currentApiVersion && w.latestImage ? (
														<span className="ml-2 text-xs text-muted-foreground">
															latest
														</span>
													) : null}
												</TableCell>
												<TableCell className="hidden md:table-cell">
													<WorkerUpdateMethod via={w.updateVia} />
												</TableCell>
												<TableCell className="text-right">
													<Button
														variant="outline"
														size="sm"
														disabled={
															!canOperate ||
															!w.canUpdate ||
															w.desiredState === "stopped" ||
															workerRoll.isPending
														}
														title={workerUpdateHint(w)}
														onClick={() => setWorkerConfirm(w)}
													>
														<CircleArrowUp className="size-4" />
														{rolling ? "Starting…" : "Update"}
													</Button>
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* ---- Confirm: self-update -------------------------------------- */}
			<ResponsiveDialog
				variant="alert"
				open={selfConfirmOpen}
				onOpenChange={setSelfConfirmOpen}
			>
				<ResponsiveDialogContent>
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>
							Update the cluster manager?
						</ResponsiveDialogTitle>
						<ResponsiveDialogDescription>
							The updater agent pulls
							{check?.latest ? (
								<span className="font-mono"> :{check.latest} </span>
							) : (
								" the latest image "
							)}
							and recreates this container. The console will briefly disconnect
							while it restarts; in-flight release operations are interrupted.
							Worker traffic is not affected.
						</ResponsiveDialogDescription>
					</ResponsiveDialogHeader>
					<ResponsiveDialogFooter>
						<ResponsiveDialogCancel disabled={selfUpdate.isPending}>
							Cancel
						</ResponsiveDialogCancel>
						<ResponsiveDialogAction
							disabled={selfUpdate.isPending}
							onClick={(e) => {
								e.preventDefault();
								selfUpdate.mutate(undefined, {
									onSettled: () => setSelfConfirmOpen(false),
								});
							}}
						>
							{selfUpdate.isPending ? "Starting…" : "Update now"}
						</ResponsiveDialogAction>
					</ResponsiveDialogFooter>
				</ResponsiveDialogContent>
			</ResponsiveDialog>

			{/* ---- Confirm: worker roll --------------------------------------- */}
			<ResponsiveDialog
				variant="alert"
				open={!!workerConfirm}
				onOpenChange={(open) => {
					if (!open) setWorkerConfirm(null);
				}}
			>
				<ResponsiveDialogContent>
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>
							Update{" "}
							<span className="font-mono">{workerConfirm?.hostname}</span>?
						</ResponsiveDialogTitle>
						<ResponsiveDialogDescription>
							The worker is drained from the load-balancer pool, its container
							is rolled to
							{workerConfirm?.latestImage ? (
								<span className="font-mono">
									{" "}
									:{workerConfirm.latestImage}{" "}
								</span>
							) : (
								" the latest image "
							)}
							and re-enabled once healthy. No new launches land on it while it
							rolls, and we wait up to two minutes for the browsers already
							running there to finish — replacing the container ends any that
							are still open.
						</ResponsiveDialogDescription>
					</ResponsiveDialogHeader>
					<ResponsiveDialogFooter>
						<ResponsiveDialogCancel disabled={workerRoll.isPending}>
							Cancel
						</ResponsiveDialogCancel>
						<ResponsiveDialogAction
							disabled={workerRoll.isPending}
							onClick={(e) => {
								e.preventDefault();
								const target = workerConfirm;
								if (!target) return;
								workerRoll.mutate(
									{ hostname: target.hostname },
									{ onSettled: () => setWorkerConfirm(null) },
								);
							}}
						>
							{workerRoll.isPending ? "Starting…" : "Drain and update"}
						</ResponsiveDialogAction>
					</ResponsiveDialogFooter>
				</ResponsiveDialogContent>
			</ResponsiveDialog>
		</div>
	);
}

export default Updates;
